import request from '@/services/request';

import type {
  ExpansionRequestApprovePayload,
  ExpansionRequestDetail,
  ExpansionRequestListParams,
  ExpansionRequestListResponse,
  ExpansionRequestRejectPayload,
  HostMini,
  HostPropertyCount,
  ImportResponse,
  MapPropertiesParams,
  MapPropertyListResponse,
  Polygon,
  PolygonListResponse,
  PolygonLookupResult,
  PolygonWritePayload,
  SimulateDecideHostPayload,
  SimulateDecideHostResult,
} from './types';


const BASE = '/api/v1/geo';


// Polygons are a small finite set (territories drawn by ops) — we want the
// whole list in one shot for the map. The backend uses StandardResultsSetPagination
// (default page_size=100), so we override with a high page_size.
const POLYGON_PAGE_SIZE = 5000;


export async function listPolygons(
  opts: { includeInactive?: boolean } = {},
): Promise<PolygonListResponse> {
  const params: Record<string, string> = {
    page_size: String(POLYGON_PAGE_SIZE),
  };
  if (opts.includeInactive) {
    params.include_inactive = 'true';
  }
  const { data } = await request.get(`${BASE}/polygons/`, { params });
  return data;
}


export async function getPolygon(id: number): Promise<Polygon> {
  const { data } = await request.get(`${BASE}/polygons/${id}/`);
  return data;
}


export async function createPolygon(payload: PolygonWritePayload): Promise<Polygon> {
  const { data } = await request.post(`${BASE}/polygons/`, payload);
  return data;
}


export async function updatePolygon(
  id: number,
  payload: Partial<PolygonWritePayload>,
): Promise<Polygon> {
  const { data } = await request.patch(`${BASE}/polygons/${id}/`, payload);
  return data;
}


export async function deletePolygon(id: number): Promise<void> {
  await request.delete(`${BASE}/polygons/${id}/`);
}


export type PolygonMergePayload = {
  source_ids: number[];
  name?: string;
};


export async function mergePolygons(
  payload: PolygonMergePayload,
): Promise<Polygon> {
  const { data } = await request.post(`${BASE}/polygons/merge/`, payload);
  return data;
}


export async function restorePolygon(id: number): Promise<Polygon> {
  const { data } = await request.post(`${BASE}/polygons/${id}/restore/`);
  return data;
}


export async function importPolygons(
  file: File,
  hostIds?: number[],
): Promise<ImportResponse> {
  const form = new FormData();
  form.append('file', file);
  if (hostIds && hostIds.length) {
    form.append('host_ids', hostIds.join(','));
  }
  const { data } = await request.post(`${BASE}/polygons/import/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}


export async function lookupPoint(
  latitude: number,
  longitude: number,
): Promise<PolygonLookupResult> {
  const { data } = await request.post(`${BASE}/lookup/`, {
    latitude,
    longitude,
  });
  return data;
}


// Query string só com o que filtra: chave vazia/indefinida/nula não viaja.
function toQueryParams(params: Record<string, unknown>): Record<string, string> {
  const cleaned: Record<string, string> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      cleaned[k] = String(v);
    }
  });
  return cleaned;
}

export async function listMapProperties(
  params: MapPropertiesParams,
): Promise<MapPropertyListResponse> {
  const { data } = await request.get(`${BASE}/properties/`, { params: toQueryParams(params) });
  return data;
}


export async function listGeoHosts(): Promise<HostMini[]> {
  const { data } = await request.get(`${BASE}/hosts/`);
  return data;
}


export async function getPolygonHostPropertyCounts(
  id: number,
): Promise<HostPropertyCount[]> {
  const { data } = await request.get(`${BASE}/polygons/${id}/host-property-counts/`);
  return data;
}

// Dry-run de `decide_host` para um endereço que ainda não é imóvel nem deal —
// nada é persistido no backend. Ver `SimulateDecideHostView`.
export async function simulateDecideHost(
  payload: SimulateDecideHostPayload,
): Promise<SimulateDecideHostResult> {
  const { data } = await request.post(`${BASE}/decide-host/simulate/`, payload);
  return data;
}

// Fila dos pedidos de ampliação (`ExpansionRequestViewSet.list`, permissão
// `IsAdmin | IsSeazone`). Ordem fixa no backend (PENDING mais antigos primeiro,
// depois fechados mais novos); filtros só `status` e `host`, e os vazios não
// viajam. Paginação `StandardResultsSetPagination` (default 100 — quem chama
// manda o `page_size` que quer).
export async function listExpansionRequests(
  params: ExpansionRequestListParams,
): Promise<ExpansionRequestListResponse> {
  const { data } = await request.get(`${BASE}/expansion-requests/`, {
    params: toQueryParams(params),
  });
  return data;
}

// Detalhe de um pedido de ampliação para a gestão de franquias decidir
// (`ExpansionRequestViewSet.retrieve`, permissão `IsAdmin | IsSeazone`).
// 404 chega como `ErrorResponse` com `statusCode` 404 — quem lê distingue
// "pedido não encontrado" de erro genérico.
export async function getExpansionRequest(id: number): Promise<ExpansionRequestDetail> {
  const { data } = await request.get(`${BASE}/expansion-requests/${id}/`);
  return data;
}

// Decisão da Seazone sobre o pedido (`ExpansionRequestViewSet.approve` /
// `.reject`, permissão `IsAdmin | IsSeazone`). Os dois respondem 200 com o
// detalhe já no estado terminal. Erros chegam como `ErrorResponse` com
// `detail = {errorCode, errorMessage, details?}`: 409 `GEO_EXPANSION_NOT_PENDING`
// (com `details.status`) quando outra pessoa decidiu antes; 400
// `GEO_EXPANSION_*` para regra quebrada ou motivo em branco. Aprovar sem
// `applied_geojson` aplica o desenho proposto.
export async function approveExpansionRequest(
  id: number,
  payload: ExpansionRequestApprovePayload,
): Promise<ExpansionRequestDetail> {
  const { data } = await request.post(`${BASE}/expansion-requests/${id}/approve/`, payload);
  return data;
}

export async function rejectExpansionRequest(
  id: number,
  payload: ExpansionRequestRejectPayload,
): Promise<ExpansionRequestDetail> {
  const { data } = await request.post(`${BASE}/expansion-requests/${id}/reject/`, payload);
  return data;
}
